import { type ReactNode, useEffect, useRef, useState } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { LinkButton } from "../components/ui/Button";
import { brand } from "../config/brand";
import { copy } from "../config/copy";
import { MessagesIndicator } from "../features/messages/MessagesIndicator";
import { NotificationBell } from "../features/notifications/NotificationBell";
import { PlayerBar, useDockVisible } from "../features/player/PlayerBar";
import { PlayerHost } from "../features/player/PlayerHost";
import { OnAirButton } from "../features/rooms/OnAirButton";
import { RoomFollower } from "../features/rooms/RoomFollower";
import { StudioSession } from "../features/studio/StudioSession";
import { Tooltip } from "./Tooltip";
import { SearchSuggest } from "../features/search/SearchSuggest";
import { useSession } from "../stores/session";
import { drag, isDesktop, noDrag, titlebarInset } from "./desktop";
import { AccountMenu } from "./AccountMenu";

/**
 * The screens that get no chrome.
 *
 * Signing in, signing up and the two token screens are single-purpose pages
 * with one thing to do on them. A header offering Discover and a dock offering
 * playback are both invitations to abandon the form, and the header's own
 * "Sign in" button on the sign-in page is faintly absurd.
 *
 * `verify-email` and `reset-password` are here too even though both are
 * reachable while signed in: they are arrived at from an email, to do one
 * thing, and then leave.
 */
const BARE = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
];

/**
 * Screens that own the whole viewport, and so get no footer.
 *
 * The console and Messages are sized to exactly the space between the header
 * and the dock, and both go to some trouble not to scroll - a footer beneath
 * them adds its own height below the fold and hands the page a scrollbar back,
 * which is the precise thing that sizing exists to remove.
 *
 * A prefix match rather than equality, because `/messages/:id` is the same
 * screen as `/messages`.
 */
const FULL_HEIGHT = ["/console", "/messages"];

/**
 * The frame every page renders inside.
 *
 * ## The rule this file exists to protect
 *
 * `<DockBar />` is a sibling of `<Outlet />`, not a child of it. React keeps a
 * component mounted as long as its position in the tree is stable, so a bar
 * outside the router outlet survives every navigation - and the single
 * `<audio>` element that will live inside it survives with it.
 *
 * That is the entire mechanism behind "playback never stops when you navigate"
 * (HANDOFF §9.1). There is no clever state-preservation trick; there is only
 * this structural fact. Anything that moves the dock inside a route, or wraps
 * the outlet in something that remounts, silently breaks the headline feature
 * in a way tests rarely catch.
 */
export function AppShell() {
  const { pathname } = useLocation();
  // Clearance is reserved only when there is something to clear.
  const dock = useDockVisible();
  const bare = BARE.includes(pathname);
  const fullHeight = FULL_HEIGHT.some(
    (path) => pathname === path || pathname.startsWith(path + "/"),
  );

  return (
    /*
      The studio's engine, wrapping everything for the same structural reason
      the audio element is down there: outside the outlet, so the router cannot
      tear it down. A broadcast used to end when the host navigated to a
      profile. It renders nothing and does nothing until the console opens it.
    */
    <StudioSession>
      <div className="flex min-h-full flex-col bg-app text-ink">
        {!bare && <TopBar />}

        {/*
        Two things at once.

        Bottom padding equal to the dock's height, or the last item of every
        scrollable page sits permanently behind the bar - the most common bug in
        this layout, and it reads as a content problem rather than a layout one.

        And centred on the bare screens, where the header and dock are both gone
        and a form pinned to the top of an empty viewport looks like a page that
        failed to finish loading.
      */}
        <main
          className={
            bare
              ? "flex flex-1 items-center justify-center"
              : dock
                ? "flex-1 pb-[calc(var(--spacing-dock)+1.5rem)]"
                : "flex-1 pb-6"
          }
        >
          <Outlet />
        </main>

        {!bare && !fullHeight && <SiteFooter />}

        {!bare && <DockBar />}

        {/*
        Outside `bare`, unlike the dock and the footer.

        Auth pages have `title`s too, and a tooltip is chrome in the way a
        navigation bar is not - there is nothing about a sign-in screen that
        wants the native one back.
      */}
        <Tooltip />

        {/*
        Keeps a followed broadcast alive across navigation, for the same
        structural reason the audio element lives here: outside the outlet,
        so the router cannot tear it down. Renders nothing unless somebody is
        actually listening to a room.
      */}
        <RoomFollower />

        {/*
        The single <audio> element, mounted once here and never unmounted.
        Renders nothing; PlayerBar above is the UI. See the note in PlayerHost
        for why the two are separate.

        Outside the `bare` check on purpose. Hiding the dock on an auth screen
        hides the *controls*; unmounting this would stop the audio, so somebody
        who opened a password-reset link mid-track would have the music cut out
        on them. The sound keeps playing and the dock reappears on the way out.
      */}
        <PlayerHost />
      </div>
    </StudioSession>
  );
}

function TopBar() {
  const user = useSession((s) => s.user);

  return (
    <header className="sticky top-0 z-20 border-b border-line bg-deep/95 backdrop-blur">
      {/*
        Full width, not capped.

        Chrome spans the viewport; content is centred within it. Capping the bar
        at 1500px gave three different alignments on a wide monitor - the bar at
        1500, a page's column at 768, and Messages edge to edge - and none of
        them lined up with each other.

        The gutters stay, so nothing touches the screen edge.
      */}
      {/*
        In the desktop shell this strip is the title bar.

        `drag` makes the window follow the pointer from here; every interactive
        thing inside then has to opt out with `noDrag` or it stops responding
        while still looking like a control. The padding is for the system's own
        window buttons, which are drawn over one end of this bar and differ by
        platform - see `app/desktop.ts`.

        In a browser all three are empty strings and this is the header it
        always was.
      */}
      <div
        className={`flex h-16 items-center gap-5 px-6 ${drag}`}
        style={
          isDesktop
            ? {
                paddingLeft: `max(1.5rem, ${titlebarInset.left}px)`,
                paddingRight: `max(1.5rem, ${titlebarInset.right}px)`,
              }
            : undefined
        }
      >
        <Link
          to="/"
          className={`flex shrink-0 items-center ${noDrag}`}
          aria-label={brand.name}
        >
          {/*
            The lockup, not a mark plus a text node. Its two-tone treatment -
            GET in Signal, VIBED in Text - lives inside the SVG, so it cannot
            drift from the brand files (§1.1).
          */}
          <img src={brand.logo} alt={brand.name} className="h-8 w-auto" />
        </Link>

        {/*
          The wrapper carries the flex sizing, not just the drag opt-out.
 
          Adding this div for the desktop title bar quietly capped the search
          box: the form inside it grows, but the div became the flex child and
          sized itself to its content, so the field stayed about 260 pixels wide
          at every window size. The classes it needs are the ones the form used
          to have.
        */}
        <div className={`hidden min-w-0 flex-[3] md:block ${noDrag}`}>
          <SearchBar />
        </div>

        {/*
          The live count, mid-bar between the search box and the nav - the one
          piece of header nothing else claims, and on the path the eye already
          takes from the logo to the account menu.

          Renders nothing when nobody is on air, which is most of the time. See
          `OnAirButton` on why a permanent "0 live" would be worse than absent.
        */}
        {/*
          `flex-1`, against the search box's `flex-[3]`.
 
          Both were `flex-1`, so they split the space between the logo and the
          nav evenly - and the search box, which is the one somebody types a
          track title into, came out at about 260 pixels. The live pill is a
          word and a number; it needs a share, not half.
        */}
        <div className={`flex flex-1 justify-center ${noDrag}`}>
          <OnAirButton />
        </div>

        <nav className={`hidden items-center gap-1 lg:flex ${noDrag}`}>
          <NavItem to="/discover">{copy["nav.discover"]}</NavItem>

          {/*
            Signed in only, both of them - and for different reasons.

            The feed needs a set of follows to assemble from, and favourites are
            by definition yours. Rendering either signed out is a link to a
            sign-in wall, which is a worse invitation than not offering it.
          */}
          {user && <NavItem to="/following">{copy["nav.following"]}</NavItem>}
          {user && <NavItem to="/favorites">{copy["nav.favorites"]}</NavItem>}

          {/*
            Rooms (Phase 7) and Library are not built. Their strings stay in
            `copy.ts` because the vocabulary is settled; the links are absent
            because a nav item that leads to "Nothing here" is worse than a
            shorter nav - it teaches people the site is broken.
          */}
        </nav>

        {user ? (
          /*
            Three things, in the order they are wanted: what is new, the thing
            you came to do, and everything about your account behind one door.

            Admin, the profile link and sign out used to sit out here. They are
            in AccountMenu now - see the note there on why a permanent sign-out
            button next to the primary action is the wrong shape.
          */
          <div
            className={`ml-auto flex shrink-0 items-center gap-2 lg:ml-0 ${noDrag}`}
          >
            <MessagesIndicator />
            <NotificationBell />

            <LinkButton to="/upload">{copy["cta.upload"]}</LinkButton>

            <AccountMenu />
          </div>
        ) : (
          /*
            `lg:ml-0`, matching the signed-in block above.

            Two siblings both carrying `ml-auto` split the free space between
            them, which is how a signed-out header ended up with "Discover"
            stranded in the middle of the bar: the nav claimed half the gap and
            the buttons the other half. Above `lg` the nav's own `ml-auto` does
            all the pushing and everything to its right sits flush against it;
            below `lg` the nav is hidden, so these need it back.
          */
          <div
            className={`ml-auto flex shrink-0 items-center gap-2 lg:ml-0 ${noDrag}`}
          >
            <LinkButton to="/login" variant="ghost">
              {copy["cta.login"]}
            </LinkButton>
            <LinkButton to="/register">{copy["cta.signup"]}</LinkButton>
          </div>
        )}
      </div>
    </header>
  );
}

function NavItem({ to, children }: { to: string; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `rounded-[var(--radius-pill)] px-3 py-2 text-sm transition-colors ${
          isActive ? "text-ink" : "text-soft hover:text-ink"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

/**
 * Search, with `/` to focus it.
 *
 * The shortcut appears in the design as a visible hint at the right of the
 * field, and it is the one keyboard affordance worth having before the
 * player's: on a discovery-led site, search is what people reach for most.
 *
 * Guarded against firing while somebody is already typing - without that check,
 * pressing `/` mid-sentence in a bio steals focus and eats the character.
 */
/**
 * The header search.
 *
 * Submits to `/search`, which owns the query in its URL - so results are
 * shareable and the back button behaves. This box only starts the search; it
 * deliberately does not keep its own copy of the term, because two places
 * holding the query is how the box and the results drift apart.
 */
function SearchBar() {
  const input = useRef<HTMLInputElement>(null);
  const wrapper = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  /*
   * The term is tracked here now, though the input is still uncontrolled.
   *
   * The suggestions need to know what has been typed, and `defaultValue` seeded
   * from the URL is what makes landing on a results page show the query that
   * produced it. Reading the value on change keeps both: React never sets it,
   * and the dropdown still follows it.
   */
  const [term, setTerm] = useState(params.get("q") ?? "");
  const [open, setOpen] = useState(false);

  /* Clicking anywhere else puts the suggestions away. */
  useEffect(() => {
    if (!open) return;

    const away = (event: PointerEvent) => {
      if (wrapper.current?.contains(event.target as Node)) return;

      setOpen(false);
    };

    document.addEventListener("pointerdown", away);

    return () => document.removeEventListener("pointerdown", away);
  }, [open]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== "/") return;

      const active = document.activeElement;
      const typing =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active instanceof HTMLElement && active.isContentEditable);

      if (typing) return;

      event.preventDefault();
      input.current?.focus();
    }

    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <form
      ref={wrapper}
      role="search"
      /*
       * Wider than it was, because the suggestions hang off it.
       *
       * `max-w-md` was sized for a box somebody types into and nothing more.
       * With results underneath it, a track title and the artist beneath it were
       * both truncating at once in a list with plenty of vertical room and none
       * across - which reads as cramped rather than as a shortcut.
       */
      className="relative w-full max-w-xl"
      onSubmit={(event) => {
        event.preventDefault();

        const typed = input.current?.value.trim() ?? "";

        if (typed === "") return;

        setOpen(false);
        navigate(`/search?q=${encodeURIComponent(typed)}`);
      }}
    >
      <span
        aria-hidden
        className="absolute left-4 top-1/2 -translate-y-1/2 text-xs text-faint"
      >
        &#9679;
      </span>
      <input
        ref={input}
        type="search"
        name="q"
        aria-label="Search"
        data-search-input
        // Uncontrolled, seeded from the URL. `defaultValue` with a key that
        // changes when the query does means landing on a results page shows
        // what was searched for, without this component owning the value.
        key={params.get("q") ?? ""}
        defaultValue={params.get("q") ?? ""}
        autoComplete="off"
        onChange={(event) => {
          setTerm(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        // Only what is actually searchable. Rooms and tags are not built, and a
        // placeholder that promises them is a bug report waiting to happen.
        placeholder="artists, tracks, albums"
        className="w-full rounded-[var(--radius-pill)] border border-line bg-slate py-2.5 pl-9 pr-12 text-sm text-ink placeholder:text-faint focus:border-line-strong"
      />
      <kbd className="meta pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded-[var(--radius-chip)] border border-line px-1.5 text-faint">
        /
      </kbd>

      <SearchSuggest
        term={term}
        open={open}
        onClose={() => setOpen(false)}
        inputRef={input}
      />
    </form>
  );
}

/**
 * The bottom of every page.
 *
 * ## Built because a form nobody can reach is not a process
 *
 * The copyright notice form existed at `/dmca` and nothing linked to it, which
 * makes it a URL rather than a route into the takedown process (§11). Safe
 * harbour assumes a claimant can *find* how to complain; a page you have to be
 * told the address of does not qualify, and neither does one only our own users
 * could stumble on.
 *
 * Deliberately small. This is not a sitemap - it is the two or three things a
 * stranger needs and cannot get anywhere else, kept quiet enough not to compete
 * with the page above it.
 */
function SiteFooter() {
  return (
    /*
      Its own dock clearance.

      `main`'s bottom padding used to be the last thing on the page and kept
      content off the fixed dock; with a footer after it, that padding now sits
      *above* the footer and the footer is what lands underneath the bar. The
      clearance has to move with the bottom of the page.
    */
    <footer
      className={`border-t border-line px-6 pt-8 ${
        // Same clearance question as `main` above, asked where the page
        // actually ends.
        useDockVisible() ? "pb-[calc(var(--spacing-dock)+2rem)]" : "pb-8"
      }`}
    >
      <div className="mx-auto flex max-w-[1500px] flex-wrap items-center gap-x-6 gap-y-2">
        <span className="meta text-ghost">
          © {new Date().getFullYear()} {brand.name}
        </span>

        <Link
          to="/terms"
          className="meta text-soft transition-colors hover:text-ink"
        >
          Terms
        </Link>

        <Link
          to="/privacy"
          className="meta text-soft transition-colors hover:text-ink"
        >
          Privacy
        </Link>

        <Link
          to="/dmca"
          className="meta text-soft transition-colors hover:text-ink"
        >
          Report a copyright claim
        </Link>

        <Link
          to="/discover"
          className="meta text-soft transition-colors hover:text-ink"
        >
          {copy["nav.discover"]}
        </Link>

        <Link
          to="/host"
          className="meta text-soft transition-colors hover:text-ink"
        >
          Host a room
        </Link>
      </div>
    </footer>
  );
}

/**
 * The persistent player and chat dock (§9.2).
 *
 * A placeholder until Phase 2 and Phase 4. It is here now, in its final
 * position in the tree, because the position is the load-bearing part - adding
 * the bar later is easy, discovering later that it has to move is not.
 */
function DockBar() {
  /*
   * The strip goes with the bar.
   *
   * This used to render unconditionally, so hiding the bar left a bordered
   * seventy-six pixel band across the bottom of the page - visibly still there,
   * just empty.
   */
  if (!useDockVisible()) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-30 h-[var(--spacing-dock)] border-t border-line bg-deep">
      <PlayerBar />
    </div>
  );
}
